import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  TextField,
  Paper,
  Autocomplete
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from 'src/apiConfig';
import PeopleIcon from '@mui/icons-material/People';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { cyrillicToLatin } from 'src/helper/helperFunction';
import { useTranslation } from 'react-i18next';

const ReviewBySchoolPage = () => {
  const [statistics, setStatistics] = useState({});
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationSearchTerm, setLocationSearchTerm] = useState(''); // For Autocomplete search
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fetchData = async () => {
    setLoading(true);
    try {
      const statisticsResponse = await axios.get(
        `${API_BASE_URL}/api/reviewAll.php?action=getInfoForReview`
      );
      setStatistics(statisticsResponse.data);

      const schoolsResponse = await axios.get(
        `${API_BASE_URL}/api/reviewAll.php?action=getSchoolsWithStudentCounts`
      );
      setSchools(schoolsResponse.data.schools || []);
      setFilteredSchools(schoolsResponse.data.schools || []);
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/locations.php`);
      setLocations(response.data || []);
    } catch (error) {
      console.error('Error fetching locations', error);
    }
  };

  useEffect(() => {
    fetchData();
    fetchLocations();
  }, []);

  const handleSchoolClick = (schoolId) => {
    navigate(`/school/${schoolId}`);
  };

  const handleLocationChange = (event, newValue) => {
    setSelectedLocation(newValue ? newValue.zipcode : '');
    filterSchools(newValue ? newValue.zipcode : '', searchTerm);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    filterSchools(selectedLocation, event.target.value);
  };

  const handleLocationSearchChange = (event, newValue) => {
    if (event && event.target) {
      setLocationSearchTerm(event.target.value);
    }
  };

  const filterSchools = (location, term) => {
    let filtered = schools;

    if (location) {
      filtered = filtered.filter((school) => school.zipcode === location);
    }

    if (term) {
      const normalizedTerm = cyrillicToLatin(term.toLowerCase());
      filtered = filtered.filter((school) => {
        const normalizedSchoolName = cyrillicToLatin(school.name.toLowerCase());
        return normalizedSchoolName.includes(normalizedTerm);
      });
    }

    setFilteredSchools(filtered);
  };

  const columns = [
    {
      field: 'name',
      headerName: t('review.school'),
      flex: 1,
      renderCell: (params) => (
        <Typography
          style={{
            textAlign: 'left',
            fontWeight: 'bold',
            color: '#1c75bc',
            cursor: 'pointer'
          }}
          onClick={() => handleSchoolClick(params.row.id)}
        >
          {params.value}
        </Typography>
      )
    },
    {
      field: 'student_count',
      headerName: t('review.student_count'),
      width: 100
    }
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        padding: '20px',
        borderRadius: '16px',
        backgroundColor: '#f7f9fc',
        margin: '20px'
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ marginBottom: '20px' }}>
        {t('review.review_by_school_title')}
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Grid container spacing={2}>
          {/* Leva strana - Tabela */}
          <Grid item xs={12} md={7}>
            <DataGrid
              rows={filteredSchools}
              columns={columns}
              getRowId={(row) => row.id}
              autoHeight
              pageSize={10}
              rowsPerPageOptions={[5, 10, 25]}
              pagination
              sx={{
                '& .MuiDataGrid-root': {
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '10px',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                  border: '1px solid #d1e3f3'
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: 'none',
                  padding: '12px',
                  fontSize: '14px',
                  color: '#333'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#2596be',
                  color: '#ffffff',
                  borderBottom: 'none',
                  borderRadius: '12px 12px 0 0'
                },
                '& .MuiDataGrid-footerContainer': {
                  backgroundColor: '#2596be',
                  color: '#ffffff',
                  borderTop: 'none',
                  borderRadius: '0 0 12px 12px'
                },
                '& .MuiCheckbox-root': {
                  color: '#28a745 !important'
                }
              }}
            />
          </Grid>

          {/* Desna strana - Pretraga, Filteri i Kartice */}
          <Grid item xs={12} md={5}>
            {/* Pretraga */}
            <TextField
              fullWidth
              label={t('review.search')}
              value={searchTerm}
              onChange={handleSearchChange}
              variant="outlined"
              sx={{ marginBottom: '20px' }}
            />

            {/* Lokacija Autocomplete */}
            <Autocomplete
              options={locations}
              getOptionLabel={(option) => option.name || ''} // Osiguraj da je `name` definisan
              inputValue={locationSearchTerm}
              onInputChange={(event, newInputValue) =>
                setLocationSearchTerm(newInputValue)
              }
              onChange={handleLocationChange}
              filterOptions={(options, state) => {
                const normalizedSearch = state.inputValue
                  ? cyrillicToLatin(state.inputValue.toLowerCase())
                  : ''; // Provera za inputValue
                return options.filter((option) => {
                  if (option && option.name) {
                    const normalizedOptionName = cyrillicToLatin(
                      option.name.toLowerCase()
                    );
                    return normalizedOptionName.includes(normalizedSearch);
                  }
                  return false;
                });
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.zipcode}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label={t('review.location')} />
              )}
              noOptionsText={t('pagination.noRowsLabel')}
              sx={{ marginBottom: '20px' }}
            />

            {/* Statističke kartice */}
            <Grid container spacing={2}>
              {[
                {
                  title: t('review.total_students'),
                  value: statistics.total_students || 0,
                  icon: <PeopleIcon sx={{ fontSize: 40, color: '#007bff' }} />
                },
                {
                  title: t('review.reviewed_students'),
                  value: statistics.reviewed_students || 0,
                  icon: (
                    <CheckCircleIcon sx={{ fontSize: 40, color: '#28a745' }} />
                  )
                }
              ].map((stat, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <Card
                    sx={{
                      backgroundColor: '#f0f4f7',
                      color: '#333',
                      padding: '20px',
                      borderRadius: '16px',
                      boxShadow: '0px 8px 16px rgba(0, 0, 0, 0.2)',
                      transition: 'transform 0.2s',
                      width: '100%',
                      height: '150px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      '&:hover': {
                        transform: 'translateY(-5px)'
                      }
                    }}
                  >
                    <CardContent>
                      <Box display="flex" alignItems="center">
                        {stat.icon}
                        <Box ml={2}>
                          <Typography variant="h6" fontWeight="bold">
                            {stat.title}
                          </Typography>
                          <Typography variant="h4">{stat.value}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default ReviewBySchoolPage;
